import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { productsData } from '../data/products';
import { CheckCircle, ArrowLeft } from 'lucide-react';

const ProductDetail = () => {
  const { productId } = useParams();
  const product = productsData[productId];

let backUrl = '/our-product';

const switchProducts = [
  'gwn7711',
  'gwn7711p',
  'gwn7700-series',
  'gwn7800-series',
  'gwn7806p',
  'gwn7810-series',
];

const infrastructureProducts = [
  'network-rack',
  'cat6-utp-patch-panel',
  'face-plate',
  'rj45-connector-plug',
  'cat6-networking-cable',
  'cat6-patch-cord',
  'cp-plus-8ch-nvr',
  'cp-plus-8ch-dvr',
  'cp-plus-3mp-wifi-camera',
  'rj45-network-crimping-tool',
  'cp-plus-full-hd-security-camera',
  'lan-cable-tester-wire-tracker',
];

if (switchProducts.includes(productId)) {
  backUrl = '/our-product?category=switches';
} else if (infrastructureProducts.includes(productId)) {
  backUrl = '/our-product?category=infrastructure';
} else {
  backUrl = '/our-product?category=firewall';
}

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <p className="text-gray-600 mb-6">The product you're looking for doesn't exist.</p>
          <Link
            to="/our-product"
            className="inline-flex items-center bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-red-600 to-red-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to={backUrl}
            className="inline-flex items-center text-red-100 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Back to Products
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{product.name}</h1>
          <p className="text-xl text-red-100">{product.tagline}</p>
        </div>
      </section>

      {/* Product Image and Description */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="bg-gray-100 rounded-xl overflow-hidden shadow-lg">
              <img src={product.image} alt={product.name} className="w-full h-auto object-cover" />
            </div>
            <div>
              <h2 className="text-3xl font-bold mb-6 text-gray-900">Overview</h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">{product.description}</p>
              {product.overview && (
                <p className="text-gray-700 leading-relaxed mb-4">{product.overview}</p>
              )}
              <Link
                to="/contact-us"
                className="inline-flex items-center bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors mt-4"
              >
                Contact Us
              </Link>
            </div>
          </div>
          {product.overview2 && (
            <div className="mt-12 max-w-5xl mx-auto">
              <p className="text-gray-700 leading-relaxed">{product.overview2}</p>
            </div>
          )}
        </div>
      </section>

      {/* Key Highlights */}
      {product.keyHighlights && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-4 text-gray-900">Key Highlights</h2>
            <div className="w-24 h-1 bg-red-600 mx-auto mb-12 rounded-full"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {product.keyHighlights.map((highlight, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-xl transition-shadow border-l-4 border-red-600"
                >
                  <div className="flex items-start">
                    <CheckCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                    <p className="ml-3 text-gray-800 font-medium">{highlight}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features Section (for products with features array) */}
      {product.features && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-4 text-gray-900">Features</h2>
            <div className="w-24 h-1 bg-red-600 mx-auto mb-12 rounded-full"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {product.features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow border border-gray-200"
                >
                  <h3 className="text-lg font-semibold mb-2 text-red-600">{feature.title}</h3>
                  <p className="text-gray-700 text-sm">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sections (for Edge Mini, Atom, 1L, Multi-WAN) */}
      {product.sections &&
        product.sections.map((section, index) => (
          <section key={index} className={`py-16 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-center mb-4 text-gray-900">{section.title}</h2>
              <div className="w-24 h-1 bg-red-600 mx-auto mb-12 rounded-full"></div>
              <div className="max-w-4xl mx-auto">
                <ul className="space-y-3">
                  {section.items.map((item, idx) => (
                    <li
                      key={idx}
                      className="flex items-start bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <CheckCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="ml-3 text-gray-800">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        ))}

      {/* Benefits Table */}
      {product.benefits && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-4 text-gray-900">
              Why Choose {product.name}?
            </h2>
            <div className="w-24 h-1 bg-red-600 mx-auto mb-12 rounded-full"></div>
            <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="grid grid-cols-2 bg-red-600 text-white">
                <div className="p-4 font-semibold text-lg border-r border-red-500">Feature</div>
                <div className="p-4 font-semibold text-lg">Benefit</div>
              </div>
              {product.benefits.map((item, index) => (
                <div
                  key={index}
                  className={`grid grid-cols-2 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-red-50 transition-colors`}
                >
                  <div className="p-4 border-r border-gray-200 text-gray-800 font-medium">
                    {item.feature}
                  </div>
                  <div className="p-4 text-gray-700">{item.benefit}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Specifications Table */}
      {product.specifications && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-4 text-gray-900">Technical Specifications</h2>
            <div className="w-24 h-1 bg-red-600 mx-auto mb-12 rounded-full"></div>
            <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
              {product.specifications.map((spec, index) => (
                <div
                  key={index}
                  className={`grid grid-cols-1 md:grid-cols-2 ${
                    index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
                  } border-b border-gray-200 last:border-b-0`}
                >
                  <div className="p-4 font-semibold text-gray-900 border-r border-gray-200">
                    {spec.label}
                  </div>
                  <div className="p-4 text-gray-700">{spec.value}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-red-100 mb-8">
            Contact our team to learn more about how {product.name} can transform your business connectivity.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact-us"
              className="inline-flex items-center justify-center bg-white text-red-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Contact Us
            </Link>
            <Link
              to="/raise-service-request"
              className="inline-flex items-center justify-center bg-red-800 text-white px-8 py-3 rounded-lg font-semibold hover:bg-red-900 transition-colors border-2 border-white"
            >
              Raise Service Request
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
